import { useState } from "react";
import styled from "styled-components";
import { Modal } from "src/apps/components/modal/Modal";
import { Select } from "src/apps/components/select/Select";
import { Text } from "src/apps/components/typography/Texts";
import { FadeInAnimation } from "src/apps/components/fadeIn/FadeInAnimation";
import { useUpdateActorFromActorTemplateId } from "src/apps/hooks/useUpdateActorTemplate";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { Button, colors } from "@permisso/permisso-components";
import { T } from "src/apps/components/i18n/Translation";
import { useTranslations } from "src/apps/hooks/useTranslations";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";

const SELECTED_TEMPLATE = "documentation_area.actor_template.selected_template";
const CHANGE_BUTTON = "documentation_area.actor_template.change_button";
const MODAL_TITLE = "documentation_area.actor_template.modal.title";
const MODAL_SELECTED_TEMPLATE = "documentation_area.actor_template.modal.selected_template";
const MODAL_SELECT_PLACEHOLDER = "documentation_area.actor_template.modal.select_placeholder";
const MODAL_CHANGE_NOTE = "documentation_area.actor_template.modal.change_note";
const CANCEL_BUTTON = "documentation_area.actor_template.modal.cancel_button";
const CONFIRM_BUTTON = "documentation_area.actor_template.modal.confirm_button";
const ERROR_MESSAGE = "documentation_area.actor_template.modal.error";
const ACTOR_TEMPLATE_CHANGE_NOT_ALLOWED_ERROR =
  "documentation_area.actor_template.modal.change_not_allowed_error";

const ERROR_MAP: Record<string, string> = {
  ACTOR_TEMPLATE_CHANGE_NOT_ALLOWED: ACTOR_TEMPLATE_CHANGE_NOT_ALLOWED_ERROR,
};

export const ActorChangeTemplateModal = () => {
  const { t } = useTranslations();
  const { documentationArea, selectedActorId } = useDocumentationArea();
  const { updateActorFromActorTemplateId, isPending, error, reset } =
    useUpdateActorFromActorTemplateId();
  const { sendEvent } = useSendAnalyticEvent();
  const availableTemplates = documentationArea?.getAvailableTemplates() ?? [];
  const selectedTemplateName = documentationArea?.getTemplateName() ?? "";
  const [pendingTemplateId, setPendingTemplateId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mappedErrorKey, setMappedErrorKey] = useState<string | null>(null);
  if (!documentationArea || availableTemplates.length === 0 || !selectedActorId) return null;

  const openModal = () => {
    setPendingTemplateId("");
    setMappedErrorKey(null);
    setIsModalOpen(true);
  };

  const handleTemplateSelection = (templateId: string) => {
    setPendingTemplateId(templateId);

    if (error) {
      reset();
      setMappedErrorKey(null);
    }
  };

  const closeModal = () => {
    if (isPending) return;

    reset();
    setPendingTemplateId("");
    setMappedErrorKey(null);
    setIsModalOpen(false);
  };

  const confirmChange = () => {
    if (!pendingTemplateId) return;

    updateActorFromActorTemplateId(
      { actorId: selectedActorId, templateId: pendingTemplateId },
      {
        onSuccess: () => {
          setPendingTemplateId("");
          setMappedErrorKey(null);
          setIsModalOpen(false);
        },
        onError: (error) => {
          sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_ACTOR_TEMPLATE_CHANGE_ERROR, {
            reason: error instanceof HTTPError ? (error.getCode() ?? "unknown") : "unknown",
            source: "DOCUMENTATION_AREA",
          });

          if (!(error instanceof HTTPError)) {
            return;
          }

          const errorCode = error.getCode();
          const mappedErrorKey = errorCode ? ERROR_MAP[errorCode] : null;

          if (mappedErrorKey) {
            setMappedErrorKey(mappedErrorKey);
          }
        },
      },
    );
  };

  const selectorContent = (
    <>
      <SelectedOption>
        <SelectedTemplateText $color={colors.grey700}>
          <T k={SELECTED_TEMPLATE} params={{ template_name: selectedTemplateName }} />
        </SelectedTemplateText>
      </SelectedOption>
      <ChangeButton type="button" onClick={openModal}>
        {t(CHANGE_BUTTON)}
      </ChangeButton>
    </>
  );

  const modalBody = (
    <ModalBody>
      <SelectedTemplateInModal>
        <T k={MODAL_SELECTED_TEMPLATE} params={{ template_name: selectedTemplateName }} />
      </SelectedTemplateInModal>
      <SelectContent>
        <Select
          value={pendingTemplateId}
          options={availableTemplates.map(({ id, name }) => ({ value: id, key: name }))}
          placeholder={t(MODAL_SELECT_PLACEHOLDER)}
          setSelectedValue={handleTemplateSelection}
        />
        {!error ? (
          <ChangeNote>
            <T k={MODAL_CHANGE_NOTE} />
          </ChangeNote>
        ) : null}
        {error ? (
          <FadeInAnimation duration={0.4} justifyContent="flex-start">
            <ErrorText>
              <T k={mappedErrorKey ?? ERROR_MESSAGE} />
            </ErrorText>
          </FadeInAnimation>
        ) : null}
      </SelectContent>
    </ModalBody>
  );

  return (
    <Container>
      {selectorContent}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={t(MODAL_TITLE)}
        body={modalBody}
        secondaryButton={
          <Button onClick={closeModal} variant="secondary" size="large" isDisabled={isPending}>
            <T k={CANCEL_BUTTON} />
          </Button>
        }
        primaryButton={
          <Button
            onClick={confirmChange}
            variant="primary"
            size="large"
            isDisabled={!pendingTemplateId || isPending || !!error}
            isLoading={isPending}
          >
            <T k={CONFIRM_BUTTON} />
          </Button>
        }
      />
    </Container>
  );
};

const SelectContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SelectedOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const SelectedTemplateText = styled(Text)`
  font-size: 0.875rem;
`;

const ChangeButton = styled.button`
  border: 0;
  padding: 0;
  color: ${colors.brandMainColor};
  background: transparent;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.875rem;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChangeNote = styled(Text)`
  color: ${colors.grey700};
  font-size: 0.875rem;
`;

const SelectedTemplateInModal = styled(Text)`
  color: ${colors.grey700};
  font-size: 0.875rem;
`;

const ErrorText = styled(Text)`
  color: ${colors.defaultRed};
  font-size: 0.875rem;
`;
